<template>
    <div class="container" v-if="product">
        <v-title :lineage="[{'Products':'products'}]">
            {{ product.id | truncate({limit: 26, dots: true}) }}
            <div slot="actions">
                <router-link
                    v-if="$auth.$pages['add-plan'].isAllowed"
                    :to="{name: 'add-plan', params: {productId: product.id, productUnit: product.unitLabel}}"
                    class="btn btn-link">Add Pricing Plan<v-icon class="margin-left-xs" icon="arrow-right"/></router-link>
            </div>
        </v-title>
        <v-inline-notifications v-if="shippingZoneRequired" type="warning" class="stack-l" permanent>
            <p slot="content">You have marked the product as shippable. You must configure <a
                target="_blank" tabindex="-1" href="https://help.rebilly.com/invoices-and-subscriptions/shipping">Shipping Zones</a> before an order can be placed. <router-link :to="{name: 'shipping-zones'}">Configure Now</router-link></p>
        </v-inline-notifications>
        <div class="grid">
            <div class="grid-item grid-item-primary">
                <div class="grid">
                    <div class="grid-item grid-item-full">
                        <div class="tile position-relative">
                            <div class="tile-title">
                                <h2>Pricing Plans</h2>
                            </div>
                            <v-inline-loader v-if="planIsLoading"/>
                            <div v-if="plans.length">
                                <pricing-plan-list
                                    v-for="plan in plans" :key="plan.id"
                                    :plan="plan"
                                    :product="product"
                                    :plans-custom-fields="plansCustomFieldsSchema"
                                    @input="updatePlan"
                                    @delete="deletePlan"/>
                            </div>
                            <div v-else>
                                <v-no-content>
                                    <span slot="title">You don't have any plans</span>
                                    <span slot="actions">
                                        <router-link
                                            v-if="$auth.$pages['add-plan'].isAllowed"
                                            :to="{name: 'add-plan', params: {productId: product.id, productUnit: product.unitLabel}}"
                                            class="btn btn-link">Add Pricing Plan<v-icon class="margin-left-xs" icon="arrow-right"/></router-link>
                                    </span>
                                </v-no-content>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-secondary">
                <div class="tile">
                    <div class="tile-title">
                        <h2>Product Info</h2>
                        <product-form-modal
                            :value="product"
                            :plans="plans"
                            :planIsLoading="planIsLoading"
                            @input="updateProduct"
                            @delete="deleteProduct"
                            :products-custom-fields="productsCustomFieldsSchema"
                            class="no-flex"/>
                    </div>
                    <div class="tile-content">
                        <p class="text-muted">Created on {{ product.createdTime | dateTime }}</p>
                        <p class="text-muted">Updated on {{ product.updatedTime | dateTime}}</p>
                    </div>
                    <div class="tile-content">
                        <table class="table table-definition table-collapsed table-v-alignment-top">
                            <colgroup>
                                <col style="width:124px;">
                                <col>
                            </colgroup>
                            <tr>
                                <td>Status</td>
                                <td>Active</td>
                            </tr>
                            <tr>
                                <td>ID</td>
                                <td v-fs-block>{{ product.id }}</td>
                            </tr>
                            <tr>
                                <td>Name</td>
                                <td v-fs-block>{{ product.name }}</td>
                            </tr>
                            <tr>
                                <td>Description</td>
                                <td v-fs-block><span class="with-breaks">{{ product.description | coverEmpty}}</span></td>
                            </tr>
                            <tr>
                                <td>Unit Label</td>
                                <td v-fs-block>{{ product.unitLabel | coverEmpty }}</td>
                            </tr>
                            <tr>
                                <td>Accounting Code</td>
                                <td v-fs-block>{{ product.accountingCode | coverEmpty }}</td>
                            </tr>
                            <tr>
                                <td>Tax Category</td>
                                <td v-fs-block>{{ getTaxCategory() }}</td>
                            </tr>
                            <tr>
                                <td>Shippable</td>
                                <td v-fs-block>{{ getShippingInfo() }}</td>
                            </tr>
                            <tr v-for="field in customFields" v-if="hasCustomFields">
                                <td v-fs-block>{{ field.name }}</td>
                                <td v-fs-block>{{ field.value }}</td>
                            </tr>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import vTitle from '@/components/v-title';
    import vIconButton from '@/components/form/v-icon-button';
    import vIcon from '@/components/form/v-icon';
    import vNoContent from '@/components/v-no-content';
    import vInlineLoader from '@/components/v-inline-loader';
    import vInlineNotifications from '@/components/v-inline-notifications';
    import ProductModel from '@/common/models/product-model';
    import PlanModel from '@/common/models/plan-model';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import CustomFieldsValuesMixin from '@/mixins/custom-fields-values-mixin';
    import taxCategories from '@/data/tax-categories';
    import pricingPlanList from './components/pricing-plan-list';
    import productFormModal from './components/product-form-modal';

    export default {
        name: 'product-details',
        mixins: [
            FormLoaderMixin,
            CustomFieldsValuesMixin,
        ],
        components: {
            vIconButton,
            vIcon,
            vTitle,
            vNoContent,
            vInlineLoader,
            vInlineNotifications,
            pricingPlanList,
            productFormModal,
        },
        props: {
            id: {
                type: [String, Number],
                default: null,
            },
        },
        data() {
            return {
                product: null,
                plans: [],
                planIsLoading: false,
                plansCustomFieldsSchema: [],
                productsCustomFieldsSchema: [],
                shippingZone: [],
            };
        },
        computed: {
            hasCustomFields() {
                return this.productsCustomFieldsSchema.length;
            },
            customFields() {
                return this.getCustomFields('product', this.getCustomFieldsTypes(this.productsCustomFieldsSchema));
            },
            shippingZoneRequired() {
                return this.product.requiresShipping && !this.shippingZone.length;
            },
        },
        methods: {
            getShippingInfo() {
                if (this.product.requiresShipping !== null) {
                    return this.product.requiresShipping === true ? 'Yes' : 'No';
                }
                return this.$emptyInnerHTML();
            },
            getTaxCategory() {
                const match = taxCategories.find(taxCategory => taxCategory.taxCode === this.product.taxCategoryId);
                if (match) {
                    return `${this.product.taxCategoryId} – ${match.description}`;
                }
                return this.$emptyInnerHTML();
            },
            async updateProduct(value) {
                this.updateOrDeleteResource('products', value);
            },
            async updatePlan(value) {
                this.updateOrDeleteResource('plans', value);
            },
            async deletePlan(value) {
                this.updateOrDeleteResource('plans', value, false);
            },
            async deleteProduct(value) {
                this.updateOrDeleteResource('products', value, false);
            },
            async updateOrDeleteResource(resource, value, isUpdate = true) {
                const payload = value.getPayload ? value.getPayload() : value;

                const delegate = isUpdate
                    ? client => client[resource].update({id: value.id, data: payload})
                    : client => client[resource].delete({id: value.id});
                const particle = isUpdate ? 'updated' : 'deleted';
                const resourceType = resource === 'products' ? 'Product' : 'Plan';
                try {
                    if (resource === 'products') {
                        this.startLoadingPage();
                    }
                    this.planIsLoading = true;
                    await this.$api(delegate);
                    if (resource === 'products' && !isUpdate) {
                        this.$router.push({name: 'products'});
                        this.$toast.positive(`${resourceType} successfully ${particle}`);
                        return;
                    }
                    await this.fetchData();
                    this.$toast.positive(`${resourceType} successfully ${particle}`);
                } catch (error) {
                    this.$toast.fromError(error, `${resourceType} data could not be ${particle}, please contact support`);
                } finally {
                    this.planIsLoading = false;
                    this.stopLoadingPage();
                }
            },
            async fetchData() {
                const params = {id: this.id};
                const [
                    {fields},
                    {items: shippingZone},
                    {items: plansCustomFieldsSchema},
                    {items: productsCustomFieldsSchema},
                ] = await Promise.all([
                    this.$api(({products}) => products.get(params)),
                    this.$api(({shippingZones}) => shippingZones.getAll({limit: 1})),
                    this.$api(({customFields}) => customFields.getAll({resource: 'plans'})),
                    this.$api(({customFields}) => customFields.getAll({resource: 'products'})),
                ]);
                this.plansCustomFieldsSchema = plansCustomFieldsSchema;
                this.productsCustomFieldsSchema = productsCustomFieldsSchema;
                this.product = new ProductModel(fields);
                this.shippingZone = shippingZone;
                const planItems = await this.$api(({plans}) => plans.getAll({filter: `productId:${this.product.id}`}));
                this.plans = planItems.items.map(plan => new PlanModel(plan.fields) || []);
            },
        },
    };
</script>
<style>
    .with-breaks {
        white-space: pre-line;
    }
</style>
